<template>
  <div class="hms-page">
    <Navigation />
    <div class="container hms-page-body">
      <div class="d-flex align-items-center gap-3 mb-4 hms-page-header">
        <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back to dashboard"><i class="mdi mdi-arrow-left"></i></a>
        <h4 class="hms-page-title m-0"><i class="mdi mdi-shield-lock-outline me-2"></i>Data Governance</h4>
      </div>

      <!-- Tabs -->
      <div class="hms-tabs mb-3">
        <button class="hms-tab" :class="{active: tab==='register'}" @click="tab='register'"><i class="mdi mdi-format-list-bulleted-type me-1"></i>Classification Register</button>
        <button class="hms-tab" :class="{active: tab==='rbac'}" @click="tab='rbac'"><i class="mdi mdi-account-key-outline me-1"></i>RBAC Matrix</button>
      </div>

      <div v-if="loading" class="hms-spinner"><div class="spinner-border"></div></div>

      <!-- ===== CLASSIFICATION REGISTER ===== -->
      <div v-else-if="tab==='register'" class="hms-card">
        <div class="hms-muted mb-2">{{ categories.length }} data categories — the live spine behind RBAC, consent and FHIR.</div>
        <div style="overflow-x:auto;">
          <table class="hms-table">
            <thead><tr>
              <th>Category</th><th>Domain</th><th>Sensitivity</th><th>FHIR</th><th>Consent</th><th>Break-glass</th><th>Retention</th>
            </tr></thead>
            <tbody>
              <tr v-for="c in categories" :key="c.category_code">
                <td><div class="fw-semibold">{{ c.category_name }}</div><div class="hms-mono hms-muted-sm">{{ c.category_code }}</div></td>
                <td><span class="hms-badge hms-badge-neutral">{{ c.domain }}</span></td>
                <td><span class="hms-badge" :class="sensClass(c.sensitivity)">{{ c.sensitivity }}</span></td>
                <td class="hms-muted-sm">{{ c.fhir_resource || '—' }}</td>
                <td><i class="mdi" :class="+c.consentable ? 'mdi-check-circle hms-text-success' : 'mdi-minus hms-muted'"></i></td>
                <td><i class="mdi" :class="+c.break_glass ? 'mdi-alert-circle hms-text-danger' : 'mdi-minus hms-muted'"></i></td>
                <td class="hms-muted-sm">{{ c.retention || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ===== RBAC MATRIX ===== -->
      <div v-else-if="tab==='rbac'" class="hms-card">
        <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
          <label class="hms-label m-0">Role</label>
          <select class="hms-input hms-filter" v-model="selectedRole" style="min-width:220px;">
            <option v-for="r in roles" :key="r.role_code" :value="r.role_code">{{ r.role_code }}</option>
          </select>
          <span class="hms-muted-sm">Set what <strong>{{ selectedRole }}</strong> can do per data category.</span>
        </div>
        <div style="overflow-x:auto;">
          <table class="hms-table">
            <thead><tr><th>Category</th><th>Sensitivity</th><th style="width:180px;">Access</th><th>Flags</th></tr></thead>
            <tbody>
              <tr v-for="c in categories" :key="c.category_code">
                <td><div class="fw-semibold">{{ c.category_name }}</div><div class="hms-mono hms-muted-sm">{{ c.category_code }}</div></td>
                <td><span class="hms-badge" :class="sensClass(c.sensitivity)">{{ c.sensitivity }}</span></td>
                <td>
                  <select class="hms-input" :value="accessFor(selectedRole, c.category_code)"
                          @change="onAccessChange(selectedRole, c.category_code, $event.target.value)"
                          :disabled="savingKey === (selectedRole + '|' + c.category_code)">
                    <option value="none">none</option>
                    <option value="view">view</option>
                    <option value="view_masked">view (masked)</option>
                    <option value="edit">edit</option>
                  </select>
                </td>
                <td class="hms-muted-sm">
                  <span v-if="+c.break_glass" class="hms-badge hms-badge-danger me-1">break-glass</span>
                  <span v-if="+c.consentable" class="hms-badge hms-badge-info">consentable</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "DataGovernance",
  components: { Navigation },
  data() {
    return {
      loading: true,
      tab: "register",
      categories: [],
      roles: [],
      accessMap: {},        // "role|category" -> access_level
      selectedRole: "",
      savingKey: "",
      toastMsg: "", toastError: false, toastTimer: null
    };
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    var t = localStorage.getItem("hms_theme") || "light";
    document.documentElement.setAttribute("data-theme", t);
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        var cats = await executeJson({ function: "getDataCategories" }, hms);
        if (cats.data && cats.data.success) this.categories = (cats.data.data && cats.data.data.Categories) || [];
        var mx = await executeJson({ function: "getCategoryAccessMatrix" }, hms);
        if (mx.data && mx.data.success && mx.data.data) {
          this.roles = mx.data.data.roles || [];
          var map = {};
          (mx.data.data.access || []).forEach(function(a){ map[a.role_code + "|" + a.category_code] = a.access_level; });
          this.accessMap = map;
          if (!this.selectedRole && this.roles.length) this.selectedRole = this.roles[0].role_code;
        }
      } catch (e) { this.showToast("Failed to load — admin access required.", true); }
      this.loading = false;
    },
    accessFor(role, cat) { return this.accessMap[role + "|" + cat] || "none"; },
    async onAccessChange(role, cat, level) {
      var key = role + "|" + cat;
      this.savingKey = key;
      try {
        var r = await executeJson({ function: "setCategoryAccess", role_code: role, category_code: cat, access_level: level }, hms);
        if (r.data && r.data.success) { this.accessMap[key] = level; this.showToast(role + " → " + cat + " = " + level); }
        else this.showToast((r.data && r.data.message) || "Save failed", true);
      } catch (e) { this.showToast("Connection error", true); }
      this.savingKey = "";
    },
    sensClass(s) {
      return { routine:"hms-badge-success", sensitive_pii:"hms-badge-info", restricted:"hms-badge-danger",
               financial:"hms-badge-warning", meta:"hms-badge-neutral", system:"hms-badge-neutral" }[s] || "hms-badge-neutral";
    },
    showToast(msg, err) {
      this.toastMsg = msg; this.toastError = !!err;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this; this.toastTimer = setTimeout(function(){ self.toastMsg = ""; }, 2800);
    }
  }
};
</script>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding: 20px 16px 40px; }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }
.hms-back { color: var(--hms-s); font-size: 22px; text-decoration: none; }
.hms-back:hover { color: var(--hms-primary); }
.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.hms-text-success { color: var(--hms-success); }
.hms-text-danger { color: var(--hms-danger); }
.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 20px; }
.hms-spinner { display: flex; justify-content: center; padding: 60px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-tabs { display: flex; gap: 8px; flex-wrap: wrap; }
.hms-tab { background: var(--hms-bg1); border: 1px solid var(--hms-br); color: var(--hms-s); border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; }
.hms-tab.active { background: var(--hms-primary); color: #fff; border-color: var(--hms-primary); }
.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); vertical-align: middle; }
.hms-input { background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 7px 12px; color: var(--hms-h); font-size: 13px; outline: none; width: 100%; }
.hms-filter { width: auto; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; }
.hms-badge { padding: 2px 8px; border-radius: 50px; font-size: 11px; font-weight: 600; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-info { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-danger { background: rgba(220,53,69,.15); color: var(--hms-danger); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: #b8860b; }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-s); }
.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>
